import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import * as projectsService from '@/services/projects';
import type { Project } from '@/services/projects';
import { ProjectContext, type ProjectContextValue } from './ProjectContext';

const ACTIVE_PROJECT_KEY = 'synter_active_project_id';

/**
 * Fetches the project list and resolves the active project from localStorage.
 * Pure async function with no React state side-effects.
 */
async function loadProjects(): Promise<{
  projects: Project[];
  activeProject: Project | null;
  error: string | null;
}> {
  const { data, error } = await projectsService.listProjects();

  if (error) {
    return { projects: [], activeProject: null, error: error.message };
  }

  const projectList = data ?? [];

  // Archived projects are returned by RLS for the unarchive window, but they
  // must never be the active project — the layout guard would bounce the user
  // straight back to /projects. Resolve active against active-only.
  const activeList = projectList.filter((p) => p.archived_at === null);

  // Resolve active project from localStorage
  const storedId = localStorage.getItem(ACTIVE_PROJECT_KEY);
  const storedProject = storedId
    ? activeList.find((p) => p.id === storedId) ?? null
    : null;

  let resolvedActive: Project | null;

  if (storedProject) {
    resolvedActive = storedProject;
  } else if (activeList.length > 0) {
    // Stale or missing localStorage: auto-switch to first non-archived project
    resolvedActive = activeList[0];
    localStorage.setItem(ACTIVE_PROJECT_KEY, activeList[0].id);
  } else {
    // Zero non-archived projects
    resolvedActive = null;
    localStorage.removeItem(ACTIVE_PROJECT_KEY);
  }

  return { projects: projectList, activeProject: resolvedActive, error: null };
}

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProjectState] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchRef = useRef(0);

  const applyResult = useCallback(
    (result: Awaited<ReturnType<typeof loadProjects>>) => {
      setProjects(result.projects);
      setActiveProjectState(result.activeProject);
      setError(result.error);
      setLoading(false);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    loadProjects().then((result) => {
      if (!cancelled) applyResult(result);
    });
    return () => {
      cancelled = true;
    };
  }, [applyResult]);

  const setActiveProject = useCallback((project: Project) => {
    setActiveProjectState(project);
    localStorage.setItem(ACTIVE_PROJECT_KEY, project.id);
  }, []);

  const refreshProjects = useCallback(async () => {
    const id = ++fetchRef.current;
    setLoading(true);
    const result = await loadProjects();
    // Only apply if this is still the latest request
    if (id === fetchRef.current) {
      applyResult(result);
    }
  }, [applyResult]);

  const value = useMemo<ProjectContextValue>(
    () => ({
      activeProject,
      projects,
      loading,
      error,
      setActiveProject,
      refreshProjects,
    }),
    [activeProject, projects, loading, error, setActiveProject, refreshProjects],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}
